/**
 * Conditional prompts for the Quick Purchase fast path (docs/03 §11, settings).
 * Only surface when they change ranking or mode — never ask for every field.
 */

import { rankCards } from './rankCards';
import type { RankCardsInput } from './types';
import type { Cents } from '../money';

export type ConditionalPrompts = {
  /** Ask whether the user will pay before statement close (UTILIZATION_HARD mitigation). */
  pay_before_statement_close: boolean;
  /** Offer maximize_protection when amount ≥ protection_prompt_threshold_cents. */
  protection_mode: boolean;
};

/**
 * Decide which optional prompts to show before ranking for real.
 * Does not invent answers — only whether to ask.
 */
export function neededConditionalPrompts(
  rankInput: RankCardsInput,
  protectionPromptThresholdCents: Cents,
): ConditionalPrompts {
  const amount = rankInput.query.amount_cents;
  const protection_mode = amount >= protectionPromptThresholdCents;

  const baseline = rankCards({
    ...rankInput,
    query: {
      ...rankInput.query,
      pay_before_statement_close: null,
    },
  });
  const mitigated = rankCards({
    ...rankInput,
    query: {
      ...rankInput.query,
      pay_before_statement_close: true,
    },
  });

  const hardWithout = baseline.ineligible.some((c) =>
    c.gate_failures.some((g) => g.code === 'UTILIZATION_HARD'),
  );
  const pay_before_statement_close =
    hardWithout && mitigated.eligible.length > baseline.eligible.length;

  return { pay_before_statement_close, protection_mode };
}
